import React, { useEffect, useRef, useState } from 'react';
import {useTranslation} from "react-i18next";

type DropDownProps = {
    options: string[];
    defaultValue: string;
    onChange?: (selectedOption: any) => void;
    width?: number;
    styles: CSSModule;
    translatePrefix?: string;
};

const SearchableDropDown: React.FC<DropDownProps> = ({
                                               options,
                                               defaultValue,
                                               onChange,
                                               width,
                                               styles,
                                               translatePrefix,
                                           }: DropDownProps): JSX.Element => {
    const [showDropDown, setShowDropDown] = useState(false);
    const [selectedValue, setSelectedValue] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const {t, i18n} = useTranslation();

    useEffect(() => {
        setSelectedValue(t(translatePrefix + "." + defaultValue).includes(translatePrefix + ".") ? defaultValue : t(translatePrefix + "." + defaultValue));
    }, [defaultValue, i18n.language]);

    // Close dropdown if click happens outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropDown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const translatedOptions: [string, string][] = options.map((option) => {
        const translatedOption = translatePrefix ?
            (t(translatePrefix + "." + option).includes(translatePrefix + ".") ? option : t(translatePrefix + "." + option))
            : option;
        return [option, translatedOption];
    });

    const filteredOptions = translatedOptions.filter((option) =>
        option[1].toLowerCase().includes(searchValue.toLowerCase())
    );

    const onClickHandler = (value: [string, string]): void => {
        setSelectedValue(value[1]);
        setSearchValue('');
        setShowDropDown(false);
        onChange?.(value[0]);
    };

    return (
        <div
            className={`${styles.DropdownWrapper}`}
            style={{ width: `${width || 228}px` }}
            ref={dropdownRef}
        >
            <div
                className={`${styles.DropdownBtn} ${showDropDown ? styles.active : ''}`}
                onClick={() => setShowDropDown((prev) => !prev)}
            >
                <div className={`${styles.DropdownInfo} ${styles.horisontalFlex} ${styles.ac}`}>
                    {showDropDown ? (
                        <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder={t("search")}
                            className={styles.DropdownSearchInput}
                            autoFocus
                            onClick={(e) => e.stopPropagation()} // prevent dropdown toggle
                        />
                    ) : (
                        <div>{selectedValue}</div>
                    )}
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 5L8 12L13 5" stroke="gray" strokeWidth="1"/>
                    </svg>
                </div>
            </div>

            {showDropDown && (
                <div className={styles.Dropdown}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <p key={index} onClick={() => onClickHandler(option)}>
                                {option[1]}
                            </p>
                        ))
                    ) : (
                        <p className={styles.NoResults}>No results found</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableDropDown;
